import type { UserSummary } from '../api/auth-client.js';

export const EMBED_MESSAGE_SOURCE = 'quartup-reports' as const;

export type ParentToIframeMessageType = 'auth:provide-token' | 'auth:request-token';

export type IframeToParentMessageType =
  | 'auth:exchange-ok'
  | 'auth:exchange-error'
  | 'notify:size-change'
  | 'notify:navigate'
  | 'download:url';

export interface ProvideTokenMessage {
  type: 'auth:provide-token';
  erpToken: string;
  requestId: string;
}

export interface RequestTokenMessage {
  type: 'auth:request-token';
  requestId: string;
}

export interface ExchangeOkMessage {
  source: typeof EMBED_MESSAGE_SOURCE;
  type: 'auth:exchange-ok';
  requestId: string;
  user: UserSummary;
}

export interface ExchangeErrorMessage {
  source: typeof EMBED_MESSAGE_SOURCE;
  type: 'auth:exchange-error';
  requestId: string;
  code: string;
  message: string;
}

export interface SizeChangeMessage {
  source: typeof EMBED_MESSAGE_SOURCE;
  type: 'notify:size-change';
  height: number;
}

export interface NavigateMessage {
  source: typeof EMBED_MESSAGE_SOURCE;
  type: 'notify:navigate';
  path: string;
}

export interface DownloadUrlMessage {
  source: typeof EMBED_MESSAGE_SOURCE;
  type: 'download:url';
  url: string;
  filename: string;
}

export type IframeOutboundMessage =
  | ExchangeOkMessage
  | ExchangeErrorMessage
  | SizeChangeMessage
  | NavigateMessage
  | DownloadUrlMessage;

export type ParentInboundMessage = ProvideTokenMessage | RequestTokenMessage;

function getAllowedOrigins(): ReadonlyArray<string> {
  const raw = import.meta.env.VITE_EMBED_ALLOWED_ORIGINS;
  if (typeof raw !== 'string' || raw.trim() === '') return [];
  return raw
    .split(',')
    .map((o) => o.trim())
    .filter((o) => o.length > 0);
}

export function isAllowedOrigin(origin: string): boolean {
  if (import.meta.env.DEV) {
    try {
      const url = new URL(origin);
      if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') return true;
    } catch {
      // not a valid URL
    }
  }

  const allowed = getAllowedOrigins();
  return allowed.includes(origin);
}

export function isParentMessage(event: MessageEvent): event is MessageEvent<ParentInboundMessage> {
  const data: unknown = event.data;
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  if (typeof d.type !== 'string') return false;
  if (!isAllowedOrigin(event.origin)) return false;

  return d.type === 'auth:provide-token' || d.type === 'auth:request-token';
}

/** Module-scoped captured origin from the first valid incoming message. */
let _parentOrigin: string | null = null;

/** Called by the message listener when the first valid parent message arrives. */
export function captureParentOrigin(origin: string): void {
  if (_parentOrigin === null) {
    _parentOrigin = origin;
  }
}

/** Exposed for tests only — resets the captured origin. */
export function _resetParentOrigin(): void {
  _parentOrigin = null;
}

/**
 * Send a message to the parent frame.
 * Uses the captured parent origin; never falls back to '*'.
 * @param message - Outbound message.
 * @param targetOrigin - Explicit override (optional). Must be provided on the very first send
 *   if no incoming message has been received yet.
 */
export function sendToParent(message: IframeOutboundMessage, targetOrigin?: string): void {
  if (window.parent === window) return;

  const origin = targetOrigin ?? _parentOrigin;
  if (origin === null) {
    // No valid parent origin captured yet — refuse to send to avoid PII leaking to '*'.
    console.warn(
      '[embed] sendToParent: parent origin not captured yet, message dropped',
      message.type,
    );
    return;
  }

  window.parent.postMessage(message, origin);
}

export function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}
